// Copyright 2025, Imprivata, Inc.  All rights reserved.

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button } from '@imprivata-cloud/design-system';
import { useSearchParams } from 'react-router-dom';

import { getLandingPage } from '../../../api/portalServices';
import type { GetLandingPageResponse, PurchasedProduct } from '../../../api/types';
import { useSession } from '../../../common/authorization/context/Session';
import { AppError } from '../../../errorHandler/errors';
import { ProductCard } from './cards/ProductCard';
import { UnpurchasedProductCard } from './cards/UnpurchasedProductCard';
import { useErrorContext } from '../../../errorHandler/context/ErrorContext';
import { ROLE_TYPE_ADMIN } from '../../constants';
import { usePasswordExpirationInfo } from '../../hooks/usePasswordExpirationInfo';
import { getDaysUntilExpiration } from '../../../common/utils/passwordExpiration';
import { useMfaEnrollmentStatus } from '../../hooks/useMfaEnrollmentStatus';
import { MfaEnrollmentBanner } from '@common/components/MfaEnrollmentBanner';
import { RootRoutes } from '@/index';
import { SelfServicePath } from '@/self-service/SelfServiceRoutes';
import { SessionStorageKeys } from '@/common/utils/sessionStorage';
import { createHrefWithCurrentSearch } from '@utils/navigationSearch';

import DefaultLogo from '../../../assets/default_logo.svg';
import './PortalLanding.less';

const TRANSLATION_ROOT = 'portal.landing.password-expiration-banner';

export const PortalLanding = () => {
  const { emitError } = useErrorContext();
  const [data, setData] = useState<GetLandingPageResponse>();
  const { tenantId, workflowId, userInfo } = useSession();
  const [isLoading, setIsLoading] = useState<boolean>();
  const { t } = useTranslation();
  const { data: passwordInfo } = usePasswordExpirationInfo();
  const [showPasswordAlert, setShowPasswordAlert] = useState(false);
  const [daysUntilExpiration, setDaysUntilExpiration] = useState<number>();
  const [searchParams] = useSearchParams();
  const mfaStatus = useMfaEnrollmentStatus();
  const isMfaPastDeadline = mfaStatus?.isPastDeadline ?? false;

  useEffect(() => {
    const getPortalInfo = async () => {
      return await getLandingPage(workflowId || '', tenantId || '');
    };
    setIsLoading(true);
    getPortalInfo()
      .then(response => {
        setData(response);
      })
      .catch(e => {
        emitError(new AppError(e));
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [workflowId, tenantId, emitError]);

  // Show (or not) password expiration banner
  useEffect(() => {
    const dismissed = sessionStorage.getItem(
      SessionStorageKeys.PASSWORD_EXPIRATION_DISMISSED_PORTAL,
    );
    const daysLeft = getDaysUntilExpiration(passwordInfo?.passwordExpiresAt);
    if (daysLeft) {
      setDaysUntilExpiration(daysLeft);
      const shouldShow = daysLeft <= 14 && daysLeft > 0 && !dismissed;
      setShowPasswordAlert(shouldShow);
    }
  }, [passwordInfo]);

  // TODO: Remove this when local testing fixes the return of "string" for logos
  const resolvedLogoUrl =
    (data?.orgInfo?.logoUrl && data?.orgInfo?.logoUrl !== 'string' && data.orgInfo.logoUrl) ||
    undefined;

  const isAdmin = userInfo?.roleType?.toLowerCase() === ROLE_TYPE_ADMIN.toLowerCase();
  const purchasedProducts = (data?.productInfo?.purchasedProducts || []).filter(
    product => product.productTag !== 'sso' || isAdmin,
  );
  const nonPurchasedProducts = (data?.productInfo?.nonPurchasedProducts || []).filter(
    product => product.productTag !== 'sso',
  );

  const passwordAlertActions = (
    <Button
      variant="default"
      size="sm"
      className="password-alert-link"
      onClick={() => {
        window.open(
          createHrefWithCurrentSearch(
            `${RootRoutes.SELF_SERVICE}${SelfServicePath.SELF_SERVICE_IDENTITY_PASSWORD}`,
            searchParams,
          ),
          '_blank',
          'noopener,noreferrer',
        );
      }}
    >
      {t(`${TRANSLATION_ROOT}.update-button`)}
    </Button>
  );

  return (
    <>
      <div className="password-expiration-alert">
        {showPasswordAlert && (
          <Alert
            status="warning"
            title={t(`${TRANSLATION_ROOT}.title`, { count: daysUntilExpiration })}
            message={t(`${TRANSLATION_ROOT}.message`)}
            actions={passwordAlertActions}
            onClose={() => {
              setShowPasswordAlert(false);
              sessionStorage.setItem(
                SessionStorageKeys.PASSWORD_EXPIRATION_DISMISSED_PORTAL,
                'true',
              );
            }}
          />
        )}
      </div>
      <MfaEnrollmentBanner />
      <section className="section title-bar content-title">
        <div className="product-container">
          <h1 className="h1">Imprivata Access Management</h1>
          {!isLoading && (
            <div>
              <img src={resolvedLogoUrl || DefaultLogo} alt="" />
            </div>
          )}
        </div>
      </section>
      {!isMfaPastDeadline && !!purchasedProducts?.length && (
        <section className="section purchased-cards">
          {purchasedProducts.map((product: PurchasedProduct) => (
            <ProductCard key={product.productTag} product={product} tenantId={tenantId || ''} />
          ))}
        </section>
      )}
      {!isMfaPastDeadline && !!nonPurchasedProducts?.length && (
        <section className="section unpurchased-cards">
          {nonPurchasedProducts.map(product => (
            <UnpurchasedProductCard key={product.productTag} product={product} />
          ))}
        </section>
      )}
    </>
  );
};
